// Loads data for details page
import {
  CategoryActionTypes,
  CategoryName,
  RootActionTypes,
  RootState,
  SharedCategoryState
} from "./userGroupDetailsTypes";
import { UserGroupDetailsView } from "./UserGroupDetailsView";
import {
  DeviceUserInfoFragment,
  useDeviceUsersListQuery,
  useGetUserGroupDetailsQuery,
  useTenantAccessorsQuery
} from "generated/graphql";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  getDeviceConnections,
  getDesktopInfo,
  getMobileInfo
} from "routes/Manage/DeviceUsers/Shared/utils/deviceUserUtils";
import { DeviceUserInfo } from "routes/Manage/DeviceUsers/Shared/utils/deviceUserUtils";
import { RelevantTenantInfo } from "routes/Admin/Accounts/Members/Helpers";

type DeviceUserInfoFragmentEdge = {
  cursor: string;
  node: DeviceUserInfoFragment;
};
export const UserGroupDetailsLoader: React.FC<{
  rootState: RootState;
  rootStateUpdate: RootActionTypes;
  groupedState: SharedCategoryState;
  groupedUpdate: CategoryActionTypes;
  availableState: SharedCategoryState;
  availableUpdate: CategoryActionTypes;
  tenant: RelevantTenantInfo;
  groupId: string;
}> = ({ tenant, groupId, availableUpdate, groupedUpdate, ...rest }) => {
  const [availableUserList, setAvailableUserList] = useState<
    Array<DeviceUserInfo>
  >([]);
  const [groupedUserList, setGroupedUserList] = useState<Array<DeviceUserInfo>>(
    []
  );
  const tenantId = tenant.id;

  const {
    data: groupQueryData,
    fetching: groupQueryFetching,
    stale: groupQueryStale
  } = useGetUserGroupDetailsQuery({
    variables: {
      tenantId,
      id: groupId
    }
  });

  const {
    data: deviceUsers,
    fetching: fetchingDeviceUsers,
    stale: deviceUsersStale
  } = useDeviceUsersListQuery({
    variables: {
      params: {
        grants: [
          {
            resourceId: tenantId,
            roles: ["device_user"]
          }
        ],
        sortBy: "name",
        searchTerm: rest.availableState.searchTerm
      },
      first: 50,
      after: rest.availableState.cursor
    }
  });

  //Get all users with access to this tenant
  const {
    data: rolesData,
    fetching: rolesFetching,
    stale: rolesStale
  } = useTenantAccessorsQuery({
    variables: {
      params: {
        resourceId: tenantId,
        resourceType: "Tenant"
      }
    }
  });

  useEffect(() => {
    // Create an array of objects with device user details
    const populateDetails = (
      userList: Array<DeviceUserInfoFragmentEdge>,
      update: Dispatch<SetStateAction<DeviceUserInfo[]>>
    ) => {
      let users = new Array<DeviceUserInfo>();
      userList?.forEach((user: DeviceUserInfoFragmentEdge) => {
        const userGrants =
          rolesData?.accessors.filter(
            accessor => accessor.id === user.node.user_id
          ) || [];

        if (userGrants?.length > 0) {
          userGrants[0]?.grants.forEach(grant => {
            if (grant.resourceId === tenantId) {
              const roles = grant.roles.map(role => role.name);
              for (const role of roles) {
                if (role === "device_user") {
                  const lensDesktopInfo = getDesktopInfo(
                    user.node.connections,
                    tenantId
                  );
                  const lensMobileInfo = getMobileInfo(
                    user.node.connections,
                    tenantId
                  );
                  const connectedDevices = getDeviceConnections(
                    lensDesktopInfo?.connections
                  );
                  users.push({
                    ...user.node,
                    connectedDevices,
                    lensDesktopInfo,
                    lensMobileInfo
                  });
                  break;
                }
              }
            }
          });
        }
      });

      if (rolesData) {
        update(users);
      }
    };

    if (deviceUsers?.deviceUsersResult?.deviceUsersInfo) {
      populateDetails(
        deviceUsers.deviceUsersResult.deviceUsersInfo,
        setAvailableUserList
      );
    }
    // convert to paginated type
    let temp: Array<DeviceUserInfoFragmentEdge> = [];
    const tempGroup =
      groupQueryData?.userGroupsForTenant?.length === 1
        ? groupQueryData?.userGroupsForTenant[0]
        : undefined;
    if (tempGroup?.genericUsers?.__typename === "GenericUserList") {
      temp =
        tempGroup?.genericUsers?.items
          ?.filter(u => u.user.user != null)
          .map(u => ({
            node: u.user.user!,
            cursor: ""
          })) ?? [];
    }
    populateDetails(temp, setGroupedUserList);
  }, [tenantId, rolesData, deviceUsers, groupQueryData]);

  useEffect(() => {
    groupedUpdate.updatePageLoading(
      CategoryName.GROUPED_USERS,
      rolesFetching || rolesStale || groupQueryFetching || groupQueryStale
    );
    availableUpdate.updatePageLoading(
      CategoryName.AVAILABLE_TO_ADD,
      rolesFetching || rolesStale || fetchingDeviceUsers || deviceUsersStale
    );
  }, [
    rolesFetching,
    rolesStale,
    fetchingDeviceUsers,
    deviceUsersStale,
    groupQueryFetching,
    groupQueryStale,
    groupedUpdate,
    availableUpdate
  ]);

  const groupData =
    groupQueryData?.userGroupsForTenant.length === 1
      ? groupQueryData?.userGroupsForTenant[0]
      : undefined;
  const pendingUsers =
    groupData?.genericUsers?.__typename === "GenericUserList"
      ? groupData?.genericUsers.items
          ?.filter(user => !user.user.user)
          .map(({ name, email }) => ({ name, email }))
      : [];
  const responseMessage =
    groupData?.genericUsers?.__typename === "GenericUserMessage"
      ? groupData.genericUsers.value
      : undefined;
  return (
    <UserGroupDetailsView
      groupData={groupData}
      groupedUsers={groupedUserList}
      groupedUpdate={groupedUpdate}
      availableUsers={availableUserList}
      availablePageInfo={deviceUsers?.deviceUsersResult?.pageInfo}
      availableUpdate={availableUpdate}
      tenant={tenant}
      responseMessage={responseMessage}
      pendingUsers={{
        loading: groupQueryFetching,
        users: pendingUsers ?? []
      }}
      {...rest}
    />
  );
};
